import { Currency, formatPercent } from '@nemuru-eng/js-api-client';
import { ReactNode } from 'react';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { EditAutoPaymentMethodSummaryRowData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodSummarySection';
import { EditAutoPaymentMethodFinancingFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import {
  buildLiveSummaryRow,
  createEditVehicleLiveSummaryFormatters,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/utils/editVehicleLiveSummaryFormatters';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';

type SpecsLiveSummaryValues = Pick<
  AutomobileFormItem,
  'brand' | 'model' | 'version' | 'identificationNumber' | 'plateNumber' | 'odometer' | 'price' | 'registrationDate'
>;

interface CreateSpecsLiveSummaryRowsParams {
  initialItem: SpecsLiveSummaryValues;
  currentItem: SpecsLiveSummaryValues;
  currency: Currency;
}

export const createSpecsLiveSummaryRows = ({
  initialItem,
  currentItem,
  currency,
}: CreateSpecsLiveSummaryRowsParams): EditAutoPaymentMethodSummaryRowData[] => {
  const { formatTextField, formatPlateNumber, formatRegistrationDate, formatOdometer, formatPrice } =
    createEditVehicleLiveSummaryFormatters();

  return [
    buildLiveSummaryRow('BRAND', formatTextField(initialItem.brand), formatTextField(currentItem.brand)),
    buildLiveSummaryRow('MODEL', formatTextField(initialItem.model), formatTextField(currentItem.model)),
    buildLiveSummaryRow('PRICING_VERSION', formatTextField(initialItem.version), formatTextField(currentItem.version)),
    buildLiveSummaryRow(
      'IDENTIFICATION_NUMBER',
      formatTextField(initialItem.identificationNumber),
      formatTextField(currentItem.identificationNumber),
    ),
    buildLiveSummaryRow(
      'LICENSE_PLATE_NUMBER',
      formatPlateNumber(initialItem.plateNumber),
      formatPlateNumber(currentItem.plateNumber),
    ),
    buildLiveSummaryRow(
      'REGISTRATION_DATE',
      formatRegistrationDate(initialItem.registrationDate),
      formatRegistrationDate(currentItem.registrationDate),
    ),
    buildLiveSummaryRow('ODOMETER', formatOdometer(initialItem.odometer), formatOdometer(currentItem.odometer), 'Km'),
    buildLiveSummaryRow(
      'VEHICLE_AMOUNT',
      formatPrice(initialItem.price, currency),
      formatPrice(currentItem.price, currency),
    ),
  ];
};

interface CreateFinancingLiveSummaryRowsParams {
  initialPrice: string | undefined;
  currentPrice: string | undefined;
  initialFinancing: FinancingLiveSummaryData;
  currentFinancing: EditAutoPaymentMethodFinancingFields;
  currency: Currency;
  getTextIdTranslation: GetTextIdTranslationType;
  termEndAdornment?: ReactNode;
}

const formatInterest = (value: number | null | undefined, notAvailable: string): string => {
  if (value === null || value === undefined) {
    return notAvailable;
  }

  return formatPercent(value);
};

const formatInsurance = (
  insuranceType: string | null | undefined,
  getTextIdTranslation: GetTextIdTranslationType,
  notAvailable: string,
): string => {
  if (!insuranceType) {
    return notAvailable;
  }

  return getTextIdTranslation(`INSURANCE_TYPE_${insuranceType}`) || insuranceType;
};

const formatSetupFee = (
  amount: number | null | undefined,
  type: string | null | undefined,
  currency: Currency,
  formatMoneyAmount: (amount: number | null | undefined, currency: Currency) => string,
  getTextIdTranslation: GetTextIdTranslationType,
  notAvailable: string,
): string => {
  if (amount === null || amount === undefined) {
    return notAvailable;
  }

  const amountLabel = formatMoneyAmount(amount, currency);

  if (!type) {
    return amountLabel;
  }

  const typeLabel = getTextIdTranslation(type) || type;

  return `${amountLabel} (${typeLabel})`;
};

const parseAmount = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = typeof value === 'number' ? value : Number.parseFloat(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const getPrincipalFromVehicleAmount = (
  vehicleAmount: string | number | null | undefined,
  discountAmount: number | null | undefined,
  downPaymentAmount: number | null | undefined,
): number | null => {
  const parsedVehicleAmount = parseAmount(vehicleAmount);

  if (parsedVehicleAmount === null) {
    return null;
  }

  return parsedVehicleAmount - (discountAmount ?? 0) - (downPaymentAmount ?? 0);
};

export const createFinancingLiveSummaryRows = ({
  initialPrice,
  currentPrice,
  initialFinancing,
  currentFinancing,
  currency,
  getTextIdTranslation,
  termEndAdornment,
}: CreateFinancingLiveSummaryRowsParams): EditAutoPaymentMethodSummaryRowData[] => {
  const { formatPrice, formatTerm, formatMoneyAmount } = createEditVehicleLiveSummaryFormatters();
  const emptyValue = '-';

  const initialPrincipalAmount = getPrincipalFromVehicleAmount(
    initialPrice,
    initialFinancing.discountAmount,
    initialFinancing.downPaymentAmount,
  );
  const currentPrincipalAmount = getPrincipalFromVehicleAmount(
    currentPrice,
    currentFinancing.discountAmount,
    currentFinancing.downPaymentAmount,
  );

  return [
    buildLiveSummaryRow('CASH_PRICE', formatPrice(initialPrice, currency), formatPrice(currentPrice, currency)),
    buildLiveSummaryRow(
      'DOWN_PAYMENT',
      formatMoneyAmount(initialFinancing.downPaymentAmount, currency),
      formatMoneyAmount(currentFinancing.downPaymentAmount, currency),
    ),
    buildLiveSummaryRow(
      'TOTAL_DISCOUNT',
      formatMoneyAmount(initialFinancing.discountAmount, currency),
      formatMoneyAmount(currentFinancing.discountAmount, currency),
    ),
    buildLiveSummaryRow(
      'PRINCIPAL_AMOUNT',
      formatMoneyAmount(initialPrincipalAmount, currency),
      formatMoneyAmount(currentPrincipalAmount, currency),
    ),
    buildLiveSummaryRow('TERM', formatTerm(initialFinancing.term), formatTerm(currentFinancing.term), termEndAdornment),
    buildLiveSummaryRow(
      'MONTHLY_QUOTA',
      formatMoneyAmount(initialFinancing.instalmentAmount, currency),
      formatMoneyAmount(currentFinancing.instalmentAmount, currency),
    ),
    buildLiveSummaryRow(
      'ANNUAL_INTEREST',
      formatInterest(initialFinancing.annualInterest, emptyValue),
      formatInterest(currentFinancing.annualInterest, emptyValue),
    ),
    buildLiveSummaryRow(
      'INSURANCE',
      formatInsurance(initialFinancing.insurance, getTextIdTranslation, emptyValue),
      formatInsurance(currentFinancing.insurance, getTextIdTranslation, emptyValue),
    ),
    buildLiveSummaryRow(
      'SETUP_FEE',
      formatSetupFee(
        initialFinancing.setupFeeAmount,
        initialFinancing.setupFeeType,
        currency,
        formatMoneyAmount,
        getTextIdTranslation,
        emptyValue,
      ),
      formatSetupFee(
        currentFinancing.setupFeeAmount,
        currentFinancing.setupFeeType,
        currency,
        formatMoneyAmount,
        getTextIdTranslation,
        emptyValue,
      ),
    ),
  ];
};
